import React, { useContext, useEffect, useState } from "react";

import { useQuery } from "@apollo/client";

import Ribbon from "@/components/common/Ribbon/Rbbon";
import RibbonPromo from "@/components/common/Ribbon/RbbonPromo";
import Search from "@/components/common/Search/Search";
import SearchContext from "@/context/SearchContext";
import UserContext from "@/context/UserContext";
import { getNavigationData } from "@/data/navigation/header.data";
import { useHeaderEffects } from "@/hooks/useHeaderEffects";
import { isCustomerAuthorized } from "@/queries/login";
import { HeaderRibbonFields } from "@/types/common";
import { PromoData } from "@/types/promoData";
import { readCookie, setCookie } from "@/utils/cookies";
import { cn } from "@/utils/general";
import { ThemeTypes } from "@/utils/theming";

import AccordionNavigation from "./AccordionNavigation";
import styles from "./Header.module.scss";
import { getDerivedHeaderFlags } from "./header.util";
import HeaderSticky from "./HeaderSticky";
import AccordionNavigationMen from "./MenAccordionNavigation";
import NavFlex from "./NavFlex";
import NavSwitch from "./NavSwitch";
import SiteSwitch from "./SiteSwitch";

// Component Props
export interface HeaderProps {
  pathname: string;
  theme: ThemeTypes;
  contentfulHeaderRibbon: HeaderRibbonFields;
  initialWithRibbon: boolean;
  promoCf: PromoData;
  initialPromoVariant: string;
}

const ctaText = "Take the Quiz";

const Header: React.FC<HeaderProps> = ({
  pathname,
  theme,
  contentfulHeaderRibbon,
  initialWithRibbon,
  initialPromoVariant,
  promoCf,
}) => {
  const [navIsOpen, setNavIsOpen] = useState<boolean>(false);
  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);
  const [isPdp, setIsPdp] = useState<boolean>(false);
  const isMen = theme === "men";
  const id = isMen ? "men" : "women";
  const promoRibbonData = promoCf?.savingsData?.web?.ribbon?.[id];
  const RibbonEl = promoRibbonData ? RibbonPromo : Ribbon;
  const [authRequestMade, setAuthRequestMade] = useState(false);
  const userState = useContext(UserContext);
  const { isSearchVisible, closeSearchDropdown } = useContext(SearchContext);
  const { headerData, accordionNavigationData } = getNavigationData(theme);
  const gatsbytoken = readCookie("gatsbytoken");
  const { loading, error, data } = useQuery(isCustomerAuthorized, {
    skip: userState.skipClientCalls || !gatsbytoken || gatsbytoken === "",
  });

  useEffect(() => {
    if (!loading) {
      if (!authRequestMade) {
        setAuthRequestMade(true);
        if (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        }
        if (data?.customer) {
          userState.logUserIn(data.customer);
        } else {
          userState.logUserOut();
          setCookie("gatsbytoken", "", 5);
        }
      }
    }
  }, [userState, loading, error, data, authRequestMade]);

  const { isMouseInHeader, onLeave, onEnter, handleNavLogoExitClick, toggleMobileNav } =
    useHeaderEffects({
      isSearchVisible,
      closeSearchDropdown,
      setIsPdp,
      navIsOpen,
      setNavIsOpen,
      setMenuIsOpen,
    });

  const { promoVariant, isAccountPage, isQuizLp, showCart, isTarget, showSiteSwitch } =
    getDerivedHeaderFlags({
      pathname,
      initialPromoVariant,
      contentfulHeaderRibbon,
    });

  /*
   * NT2-30485: hideOnTarget hides ribbon on the Contentful ribbonLink path.
   * Computed synchronously so the ribbon SSR-mounts and does not cause CLS.
   */
  const withRibbon =
    promoRibbonData?.hideOnTarget && isTarget ? false : initialWithRibbon || isTarget;

  if (theme === "app") {
    return null;
  }

  return (
    <header
      data-mousedown
      className={cn(styles.headerWrap, "header-wrap", {
        "nav-open ": navIsOpen,
        [styles["nav-open"]]: navIsOpen,
        [styles["has-ribbon"]]: withRibbon,
        [styles["is-account-page"]]: isAccountPage,
      })}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onTouchEnd={() => {
        setTimeout(() => {
          isMouseInHeader.current = false;
        });
      }}
      onClick={(e) => {
        if (isSearchVisible) {
          e.stopPropagation();
        }
        isMouseInHeader.current = false;
      }}
    >
      {/*
       * Mobile: sticky header keeps nav + ribbon visible until pagescrolled (~85px),
       * then the in-flow ribbon eases closed (max-height/opacity transition).
       */}
      <div className="header-pin">
        {!isAccountPage ? (
          withRibbon && contentfulHeaderRibbon ? (
            <div className="ribbon-container hidden lg:flex flex-wrap relative bg-transparent">
              {showSiteSwitch ? (
                <>
                  <div className="w-full min-h-[35px] md:w-[200px]">
                    <SiteSwitch theme={theme} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <RibbonEl
                      contentfulHeaderRibbon={contentfulHeaderRibbon}
                      isMouseInHeader={isMouseInHeader}
                      PROMO_VARIANT={promoVariant}
                      pathname={pathname}
                      theme={theme}
                      promoCf={promoCf}
                    />
                  </div>
                </>
              ) : (
                <div className="hidden lg:block lg:w-full">
                  <RibbonEl
                    contentfulHeaderRibbon={contentfulHeaderRibbon}
                    isMouseInHeader={isMouseInHeader}
                    PROMO_VARIANT={promoVariant}
                    pathname={pathname}
                    theme={theme}
                    promoCf={promoCf}
                  />
                </div>
              )}
            </div>
          ) : (
            <div
              className={`no-ribbon-switch border-r border-r-nova-transparency-black-20 ${showSiteSwitch ? "" : "!hidden"}`}
            >
              <div className="w-full hidden lg:block min-h-[35px] md:w-[200px]">
                <SiteSwitch theme={theme} className={``} />
              </div>
            </div>
          )
        ) : null}

        <div className={cn(`container-${isMen ? "mens" : "master"}`, styles.headerContainer)}>
          <div className="stickyatcheader">
            <HeaderSticky
              theme={theme}
              handleNavLogoExitClick={handleNavLogoExitClick}
              useOneTimeOption={true}
            />
          </div>
          <div className={cn("wrap nonstickyheader", styles.contentsContainer)}>
            {isAccountPage ? (
              <>todo</>
            ) : (
              <>
                <div className={styles["mobile-nav-flex"]}>
                  <div className={cn(styles.navMobile, "flex flex-wrap items-center w-full")}>
                    {!isMen ? (
                      <NavSwitch type="men" copy="Men" />
                    ) : (
                      <NavSwitch type="women" copy="Women" />
                    )}
                    {userState?.isLoggedIn ? (
                      <NavSwitch type="account" copy="My Account" />
                    ) : (
                      <NavSwitch type={isMen ? "menquiz" : "quiz"} copy={ctaText} />
                    )}
                  </div>
                </div>
                <NavFlex
                  promoCf={promoCf}
                  theme={theme}
                  data={headerData}
                  isAccountPage={isAccountPage}
                  navIsOpen={navIsOpen}
                  isPdp={isPdp}
                  isQuizLp={isQuizLp}
                  handleNavLogoExitClick={handleNavLogoExitClick}
                  showCart={showCart}
                  toggleMobileNav={toggleMobileNav}
                  isIconVisibleOnRightSideOnly
                />
              </>
            )}
          </div>

          {userState?.isLoggedIn ? null : null}
        </div>

        {withRibbon && contentfulHeaderRibbon && !isAccountPage ? (
          <div className="lg:hidden ribbon-container ribbon-container-mobile">
            <RibbonEl
              contentfulHeaderRibbon={contentfulHeaderRibbon}
              isMouseInHeader={isMouseInHeader}
              PROMO_VARIANT={promoVariant}
              pathname={pathname}
              isMobile
              theme={theme}
              promoCf={promoCf}
            />
          </div>
        ) : null}
      </div>

      {navIsOpen ? (
        !isMen ? (
          <AccordionNavigation
            promoCf={promoCf}
            data={accordionNavigationData}
            toggleNav={setNavIsOpen}
            navIsOpen={navIsOpen}
            menuIsOpen={menuIsOpen}
          />
        ) : (
          <AccordionNavigationMen
            promoCf={promoCf}
            data={accordionNavigationData}
            toggleNav={setNavIsOpen}
            navIsOpen={navIsOpen}
            menuIsOpen={menuIsOpen}
          />
        )
      ) : null}

      {isSearchVisible && <Search />}
    </header>
  );
};

export default Header;
